import axios from 'axios'
import { ElLoading, ElMessage } from 'element-plus'
import { saveAs } from 'file-saver'
import { getToken } from '@/utils/auth'
import errorCode from '@/utils/errorCode'
import { blobValidate } from '@/utils/ruoyi'

const baseURL = import.meta.env.VITE_APP_BASE_API
let downloadLoadingInstance;


export default {
  async name(name, isDelete = true) {
    const url = baseURL + "/common/download?fileName=" + encodeURIComponent(name) + "&delete=" + isDelete;

    // Enhanced MIME type mapping with more extensions
    const mimeTypes = {
      'dxf': 'application/dxf',
      'txt': 'text/plain',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'zip': 'application/zip',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'svg': 'image/svg+xml',
      // Add more as needed
    };

    const hasNativePicker = typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function';

    // Prepare default filename, ext and mime using the provided name first
    let defaultFilename = decodeURIComponent(name || 'download');
    if (!defaultFilename.includes('.')) {
      defaultFilename += '';
    }
    const defaultExt = (defaultFilename.split('.').pop() || '').toLowerCase();
    const defaultMime = mimeTypes[defaultExt] || 'application/octet-stream';

    let handle = null;
    if (hasNativePicker) {
      try {
        const typeDesc = (defaultExt || '').toUpperCase() + (defaultExt ? ' 文件' : '');
        const options = {
          suggestedName: defaultFilename,
          excludeAcceptAllOption: false,
          types: [
            {
              description: typeDesc || '文件',
              accept: {
                [defaultMime]: defaultExt ? [`.${defaultExt}`] : ['']
              }
            }
          ]
        };
        try { options.startIn = 'desktop'; } catch (e) { /* no-op */ }
        handle = await window.showSaveFilePicker(options);
      } catch (e) {
        if (e && e.name === 'AbortError') {
          // user cancelled explicitly
          return;
        }
        // If opening dialog failed for other reasons, fall back later
        handle = null;
        try { ElMessage.info('保存对话框被浏览器拦截或不可用，已使用默认下载方式'); } catch (_) {}
      }
    } else {
      try { ElMessage.info('当前浏览器不支持保存对话框，已使用默认下载方式'); } catch (_) {}
    }

    axios({
      method: 'get',
      url: url,
      responseType: 'blob',
      headers: {'Authorization': 'Bearer ' + getToken()}
    }).then(async (res) => {
      const isBlob = blobValidate(res.data);
      if (!isBlob) {
        this.printErrMsg(res.data);
        return;
      }

      // Determine final filename and mime from response if possible
      let filename = decodeURIComponent(res.headers['download-filename'] || defaultFilename);
      if (!filename.includes('.')) {
        const originalExt = defaultExt;
        if (originalExt && originalExt.length <= 5) {
          filename += '.' + originalExt;
        }
      }
      const finalExt = (filename.split('.').pop() || '').toLowerCase();
      const finalMime = mimeTypes[finalExt] || defaultMime;
      const blob = new Blob([res.data], { type: finalMime });

      // If we already have a handle (user chose path), write to it; else fallback
      if (handle) {
        try {
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (e) {
          // If writing fails, fallback to traditional download
        }
      }
      this.saveAs(blob, filename);
    }).catch(error => {
      console.error('Download failed:', error);
      // Handle error appropriately
    });
  },
  async promptAndSave(blob, filename, mimeType, ext) {
    const pickResult = await this.pickSaveLocation(filename, mimeType, ext)
    if (pickResult.mode === 'cancelled') {
      return false
    }
    return this.saveBlobWithPickResult(pickResult, blob, filename)
  },
  async pickSaveLocation(filename, mimeType, ext) {
    const hasNativePicker = typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function'
    if (!hasNativePicker) {
      return { mode: 'fallback' }
    }
    try {
      const typeDesc = (ext || '').toUpperCase() + (ext ? ' 文件' : '')
      const options = {
        suggestedName: filename,
        excludeAcceptAllOption: false,
        types: [
          {
            description: typeDesc || '文件',
            accept: {
              [mimeType || 'application/octet-stream']: ext ? [`.${ext}`] : ['']
            }
          }
        ]
      }
      try {
        options.startIn = 'desktop'
      } catch (e) {
        // no-op
      }
      const handle = await window.showSaveFilePicker(options)
      return { mode: 'handle', handle }
    } catch (e) {
      if (e && e.name === 'AbortError') {
        return { mode: 'cancelled' }
      }
      return { mode: 'fallback' }
    }
  },
  async pickSaveDirectory() {
    const hasDirectoryPicker = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function'
    if (!hasDirectoryPicker) {
      return { mode: 'fallback' }
    }
    try {
      const options = { mode: 'readwrite' }
      try {
        options.startIn = 'desktop'
      } catch (e) {
        // no-op
      }
      const handle = await window.showDirectoryPicker(options)
      await this.ensureDirectoryPermission(handle)
      return { mode: 'directory', handle }
    } catch (e) {
      if (e && e.name === 'AbortError') {
        return { mode: 'cancelled' }
      }
      return { mode: 'fallback' }
    }
  },
  async ensureDirectoryPermission(dirHandle) {
    if (!dirHandle?.queryPermission) {
      return dirHandle
    }
    const options = { mode: 'readwrite' }
    let permission = await dirHandle.queryPermission(options)
    if (permission !== 'granted') {
      permission = await dirHandle.requestPermission(options)
    }
    if (permission !== 'granted') {
      throw new Error('未获得文件夹写入权限')
    }
    return dirHandle
  },
  async saveBlobToDirectory(dirHandle, filename, blob) {
    await this.ensureDirectoryPermission(dirHandle)
    const fileHandle = await dirHandle.getFileHandle(filename, { create: true })
    const writable = await fileHandle.createWritable()
    await writable.write(blob)
    await writable.close()
  },
  async saveBlobToDirectoryNested(dirHandle, relativePath, blob, dirCache = new Map()) {
    await this.ensureDirectoryPermission(dirHandle)
    const normalizedPath = String(relativePath || '').replace(/\\/g, '/')
    const parts = normalizedPath.split('/').filter(Boolean)
    if (parts.length === 0) {
      throw new Error('无效的文件路径')
    }

    let currentHandle = dirHandle
    const dirParts = parts.slice(0, -1)
    for (let i = 0; i < dirParts.length; i++) {
      const cacheKey = dirParts.slice(0, i + 1).join('/')
      if (dirCache.has(cacheKey)) {
        currentHandle = dirCache.get(cacheKey)
      } else {
        currentHandle = await currentHandle.getDirectoryHandle(dirParts[i], { create: true })
        dirCache.set(cacheKey, currentHandle)
      }
    }

    const fileHandle = await currentHandle.getFileHandle(parts[parts.length - 1], { create: true })
    const writable = await fileHandle.createWritable()
    await writable.write(blob)
    await writable.close()
    return dirCache
  },
  async saveBlobWithPickResult(pickResult, blob, fallbackFilename) {
    if (pickResult.mode === 'cancelled') {
      return false
    }
    if (pickResult.mode === 'handle') {
      const writable = await pickResult.handle.createWritable()
      await writable.write(blob)
      await writable.close()
      return true
    }
    this.saveAs(blob, fallbackFilename)
    return true
  },
  resource(resource) {
    var url = baseURL + "/common/download/resource?resource=" + encodeURIComponent(resource);
    axios({
      method: 'get',
      url: url,
      responseType: 'blob',
      headers: { 'Authorization': 'Bearer ' + getToken() }
    }).then((res) => {
      const isBlob = blobValidate(res.data);
      if (isBlob) {
        const blob = new Blob([res.data])
        this.saveAs(blob, decodeURIComponent(res.headers['download-filename']))
      } else {
        this.printErrMsg(res.data);
      }
    })
  },
  zip(url, name) {
    var url = baseURL + url
    downloadLoadingInstance = ElLoading.service({ text: "正在下载数据，请稍候", background: "rgba(0, 0, 0, 0.7)", })
    axios({
      method: 'get',
      url: url,
      responseType: 'blob',
      headers: { 'Authorization': 'Bearer ' + getToken() }
    }).then((res) => {
      const isBlob = blobValidate(res.data);
      if (isBlob) {
        const blob = new Blob([res.data], { type: 'application/zip' })
        this.saveAs(blob, name)
      } else {
        this.printErrMsg(res.data);
      }
      downloadLoadingInstance.close();
    }).catch((r) => {
      console.error(r)
      ElMessage.error('下载文件出现错误，请联系管理员！')
      downloadLoadingInstance.close();
    })
  },
  saveAs(text, name, opts) {
    saveAs(text, name, opts);
  },
  async printErrMsg(data) {
    const resText = await data.text();
    const rspObj = JSON.parse(resText);
    const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
    ElMessage.error(errMsg);
  }
}

